import React, { useContext, useState } from 'react';
import i18next from 'core/i18n';
import {
  Button,
  TinyTitle,
  useFlashMessage,
  Select,
  Divider,
  ReadOnlyField,
  MediumTitle
} from '@indigo-ag/component-module';
import { faCloudUpload } from '@fortawesome/pro-regular-svg-icons';
import {
  AgFinancingFilesContext,
  AgFinancingDocumentType,
  AgFinancingFile,
  ContractStatus,
  Phase3,
  AgFinancing
} from '@indigo-ag/schema';
import styled from 'styled-components';
import { Dictionary } from 'lodash';

import {
  AttachButton,
  DocumentCard,
  UploadDocumentCard,
  amendmentRequestTypesOptions,
  documentRequestTypesTranslations,
  PhaseSection,
  EditableTextField,
  EditableTextArea
} from 'shared';
import {
  ProgressDictionary,
  removeFilesFromAgFinancing
} from 'features/brazil-grower/components/BrazilGrowerFinance/utils';
import { text, usePrompt } from 'shared/utils';
import { CollateralFormalizationContext } from '../../CollateralAnalysis/CollateralFormalization/CollateralFormalization';
import { ExecutionResult } from 'graphql';
import { AmendmentSignatureRequest } from './AmendmentSignatureRequest/AmendmentSignatureRequest';

const StyledSelect = styled(Select)`
  margin-bottom: 24px;
  margin-right: 20px;
  max-height: 54px;
  width: 180px;
`;

interface AmendmentSectionProps {
  phase3Info: Phase3;
}

export const AmendmentSection: React.FunctionComponent<AmendmentSectionProps> = ({ phase3Info }) => {
  const { contractStatus } = phase3Info;

  /**
   * State
   */
  const [instructions, setInstructions] = useState(phase3Info.amendmentInstructions || '');
  const [registrationNumber, setRegistrationNumber] = useState(phase3Info.amendmentRegistrationNumber || '');
  const [savingAmendmentInfo, setSavingAmendmentInfo] = useState(false);
  const [isUpdatingInformation, setIsUpdatingInformation] = useState(false);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<ProgressDictionary>();
  const [documentTypeDictionary, setUpdateDocumentType] = useState<Dictionary<AgFinancingDocumentType>>({});
  const [filesIdsToRemove, setFilesIdsToRemove] = useState<AgFinancingFile['id'][]>([]);
  const [amendmentFiles, setAmendmentFiles] = useState<AgFinancingFile[]>(
    phase3Info?.files?.filter(f => f.context === AgFinancingFilesContext.AMENDMENT_DOCUMENTS) || []
  );
  const amendmentSignFiles =
    phase3Info?.files?.filter(f => f.context === AgFinancingFilesContext.AMENDMENT_SIGN_DOCUMENTS) || [];

  /**
   * Context
   */
  const { updatePhase3, uploadFilesToPhase3 } = useContext(CollateralFormalizationContext);

  const { addFlashMessage } = useFlashMessage();

  usePrompt(isUpdatingInformation);

  const updateInstructions = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setInstructions(event.target.value);
    setIsUpdatingInformation(true);
  };

  const handleRegistrationNumber = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRegistrationNumber(event.target.value);
    setIsUpdatingInformation(true);
  };

  const handleFilesSelected = (files: File[]) => {
    setNewFiles(files);
    setIsUpdatingInformation(true);
  };

  const removeNewFile = (fileId: string) => {
    setNewFiles(newFiles.filter(f => f['newFileId'] !== fileId));
    setIsUpdatingInformation(true);
  };

  const handleUpdateDocumentType = (fileId: string) => {
    return (newDocumentType: string | null) => {
      if (newDocumentType) {
        setUpdateDocumentType({ ...documentTypeDictionary, [fileId]: newDocumentType as AgFinancingDocumentType });
      }
    };
  };

  const saveAmendmentInfo = () => {
    setSavingAmendmentInfo(true);

    removeFilesFromAgFinancing(filesIdsToRemove)
      .then(() =>
        uploadFilesToPhase3(
          newFiles,
          AgFinancingFilesContext.AMENDMENT_DOCUMENTS,
          documentTypeDictionary,
          percentCompleted => setProgress({ ...percentCompleted })
        )
      )
      .then(filesUploaded =>
        updatePhase3(
          {
            amendmentRegistrationNumber: registrationNumber,
            amendmentInstructions: instructions
          },
          filesUploaded.fulfilled as AgFinancingFile[]
        )
      )
      .then((r: ExecutionResult<{ upsertFinancingAgFinancing: AgFinancing }>) => {
        addFlashMessage({
          content: text.firstLetterUppercase(i18next.t('amendmentInformationSavedSuccessfully')),
          title: '',
          variant: 'success'
        });
        setIsUpdatingInformation(false);
        setNewFiles([]);
        setAmendmentFiles(
          r.data?.upsertFinancingAgFinancing?.phase3?.files?.filter(
            f => f.context === AgFinancingFilesContext.AMENDMENT_DOCUMENTS
          ) || []
        );
      })
      .catch(() => {
        addFlashMessage({
          content: i18next.t('error'),
          title: '',
          variant: 'error'
        });
      })
      .finally(() => {
        setSavingAmendmentInfo(false);
      });
  };

  const removeSavedFile = (fileId: AgFinancingFile['id']) => {
    return () => {
      setAmendmentFiles([...amendmentFiles.filter(f => f.id !== fileId)]);
      setFilesIdsToRemove([...filesIdsToRemove, fileId]);
      setIsUpdatingInformation(true);
    };
  };

  const editable = contractStatus === ContractStatus.CREATING_AMENDMENT;

  const isSaveDisabled = !!(
    !isUpdatingInformation ||
    !registrationNumber ||
    (newFiles.length && newFiles.some(f => !documentTypeDictionary[f['newFileId']]))
  );

  const FileDisplay = ({ file }: { file: AgFinancingFile }) => (
    <div key={file.id} className="flex flex-column">
      <TinyTitle className="mt-1 text-transform-capitalize">
        {documentRequestTypesTranslations[file.documentType]}
      </TinyTitle>
      <DocumentCard document={file} onDelete={editable ? removeSavedFile(file.id) : undefined} />
    </div>
  );

  return (
    <PhaseSection
      title={i18next.t('amendmentCreation')}
      subtitle={i18next.t('amendmentCreationSubtitle')}
      updatedAt={phase3Info.amendmentInformationUpdatedAt}
    >
      <EditableTextField
        label={i18next.t('amendmentRegistrationNumber')}
        value={registrationNumber}
        onChange={handleRegistrationNumber}
        readonly={!editable}
      />
      <EditableTextArea
        label={`${i18next.t('requirements')} (${i18next.t('optional')})`}
        value={instructions}
        onChange={updateInstructions}
        readonly={!editable}
      />
      <div className="flex flex-column mt-4">
        {amendmentFiles?.map(file => (
          <FileDisplay key={file.id} file={file} />
        ))}
        {!!amendmentSignFiles.length && (
          <>
            <Divider className="mt-4 mb-4" />
            <MediumTitle className="mb-4">{i18next.t('amendmentSignedFiles')}</MediumTitle>
            {!!phase3Info.amendmentSignedNotes && (
              <ReadOnlyField label={i18next.t('commentsOrRecommendations')} value={phase3Info.amendmentSignedNotes} />
            )}
            {amendmentSignFiles?.map(file => (
              <FileDisplay key={file.id} file={file} />
            ))}
          </>
        )}
      </div>
      {editable && !!newFiles.length && (
        <div className="flex flex-column">
          {newFiles.map(file => (
            <div className="flex align-items-center" key={file['newFileId']}>
              <StyledSelect
                key={file['newFileId']}
                sizeVariant="slim"
                label={i18next.t('documentType')}
                name="documentType"
                options={amendmentRequestTypesOptions}
                value={documentTypeDictionary[file['newFileId']]}
                onSelectionChanged={handleUpdateDocumentType(file['newFileId'])}
                placeholder={i18next.t('pleaseSelect')}
                error={!documentTypeDictionary[file['newFileId']]}
              />
              <div style={{ maxWidth: 'calc(100% - 200px)' }}>
                <UploadDocumentCard
                  documentName={file.name}
                  progress={progress ? progress[file['newFileId']] : undefined}
                  onRemove={() => removeNewFile(file['newFileId'])}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      {editable && (
        <>
          <div className="flex flex-space-btw">
            <AttachButton
              onFilesSelected={handleFilesSelected}
              size="small"
              faIconAfter={faCloudUpload}
              variant="tertiary"
            >
              {i18next.t('uploadFiles')}
            </AttachButton>
            <Button size="small" disabled={isSaveDisabled} onClick={saveAmendmentInfo} busy={savingAmendmentInfo}>
              {i18next.t('save')}
            </Button>
          </div>
          <AmendmentSignatureRequest disabled={!registrationNumber || !amendmentFiles.length} />
        </>
      )}
    </PhaseSection>
  );
};
